import React from 'react';
import { useListContext, useResourceContext, useRedirect } from 'react-admin';
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from './table';
import { cn } from '../../lib/utils';

/**
 * Latio DataTable — shadcn Table driven by React Admin's ListContext.
 * Keeps RA's server-side data/sort/pagination; provides the design's look:
 * uppercase headers, indigo sortable arrows, row→detail, inline action cells.
 * DESIGN_SYSTEM.md §6/§7. Must be rendered inside a React Admin <List>.
 */
export type DataColumn<T = any> = {
  /** Unique column key. */
  key: string;
  /** Uppercase header label. */
  header: string;
  /** Enable click-to-sort (wired to RA setSort). */
  sortable?: boolean;
  /** RA sort field if different from `key`. */
  sortField?: string;
  align?: 'left' | 'right';
  /** Optional fixed width, e.g. '1px' to shrink an ID column. */
  width?: string;
  className?: string;
  cell: (record: T) => React.ReactNode;
};

type DataTableProps<T> = {
  columns: DataColumn<T>[];
  /** RA action for row click; false disables navigation. */
  rowClick?: 'show' | 'edit' | false;
  /** Override the row destination. */
  getRowLink?: (record: T) => string;
  emptyMessage?: string;
};

function SortIndicator({ active, order }: { active: boolean; order?: 'ASC' | 'DESC' }) {
  if (active) {
    return order === 'ASC' ? (
      <ArrowUp className="size-3.5 text-indigo" />
    ) : (
      <ArrowDown className="size-3.5 text-indigo" />
    );
  }
  return <ChevronsUpDown className="size-3.5 text-ink-3/60" />;
}

export function DataTable<T extends { id: any }>({
  columns,
  rowClick = 'show',
  getRowLink,
  emptyMessage = 'No records found.',
}: DataTableProps<T>) {
  const { data, isLoading, sort, setSort } = useListContext<T>();
  const resource = useResourceContext();
  const redirect = useRedirect();

  const handleSort = (col: DataColumn<T>) => {
    if (!col.sortable) return;
    const field = col.sortField || col.key;
    const nextOrder =
      sort?.field === field && sort?.order === 'ASC' ? 'DESC' : 'ASC';
    setSort({ field, order: nextOrder });
  };

  const handleRowClick = (record: T) => {
    if (rowClick === false) return;
    if (getRowLink) {
      redirect(getRowLink(record));
      return;
    }
    redirect(rowClick, resource, record.id);
  };

  const headerCls =
    'h-11 px-4 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2';

  return (
    <div className="overflow-hidden rounded-card border border-hairline bg-background">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-hairline bg-surface-2 hover:bg-surface-2">
            {columns.map((col) => (
              <TableHead
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={cn(
                  headerCls,
                  col.align === 'right' && 'text-right',
                  col.className,
                )}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => handleSort(col)}
                    className={cn(
                      'inline-flex items-center gap-1.5 uppercase tracking-[0.06em] hover:text-ink',
                      col.align === 'right' && 'flex-row-reverse',
                    )}
                  >
                    {col.header}
                    <SortIndicator
                      active={sort?.field === (col.sortField || col.key)}
                      order={sort?.order as 'ASC' | 'DESC'}
                    />
                  </button>
                ) : (
                  col.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (!data || data.length === 0) ? (
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`} className="border-b border-hairline">
                {columns.map((col) => (
                  <TableCell key={col.key} className="px-4 py-3">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-surface-2" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : !data || data.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="py-16 text-center text-sm text-ink-2"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((record) => (
              <TableRow
                key={record.id}
                onClick={() => handleRowClick(record)}
                className={cn(
                  'border-b border-hairline transition-colors duration-150',
                  rowClick !== false && 'cursor-pointer hover:bg-[#F7F7F8]',
                )}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'px-4 py-3 align-middle text-sm text-ink',
                      col.align === 'right' && 'text-right',
                      col.className,
                    )}
                  >
                    {col.cell(record)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
